'use client';

import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cn } from './cn';
import { BUTTON_BASE, BUTTON_VARIANT, type ButtonVariant, type ButtonSize } from './Button';
import { Spinner } from './Spinner';

export interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Required: icon-only controls must be labelled. */
  label: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Circular instead of the standard 8 px radius — used for canvas/overlay controls. */
  round?: boolean;
  loading?: boolean;
  asChild?: boolean;
  children: React.ReactNode;
}

const ICON_SIZE: Record<ButtonSize, string> = {
  sm: 'k-hit size-9 [&_svg]:size-4',
  md: 'size-touch [&_svg]:size-[1.125rem]',
  lg: 'size-13 [&_svg]:size-5',
};

/**
 * Square/round icon-only button. `label` becomes `aria-label` — pair it with a
 * `<Tooltip>` for sighted users; the two should say the same thing.
 */
export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, variant = 'ghost', size = 'md', round, loading, asChild, className, children, disabled, type, ...props },
  ref,
) {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      ref={ref}
      type={asChild ? undefined : (type ?? 'button')}
      aria-label={label}
      title={undefined}
      disabled={asChild ? undefined : disabled || loading}
      aria-busy={loading || undefined}
      data-variant={variant}
      className={cn(
        BUTTON_BASE,
        BUTTON_VARIANT[variant],
        ICON_SIZE[size],
        'p-0',
        round && 'rounded-full',
        className,
      )}
      {...props}
    >
      {loading ? <Spinner size={size === 'sm' ? 'xs' : 'sm'} /> : children}
    </Comp>
  );
});
